import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { X, Loader2, Printer, Warehouse } from 'lucide-react';
import { etiquetaPallet, imprimirEtiquetasQr } from './armazemUtils';

const PalletsRecebimentoModal = ({ recebimento, empresaId, onClose }) => {
  const [numero, setNumero] = useState(1);
  const [items, setItems] = useState([]);
  const [sugestoes, setSugestoes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'listArmazemPallets');
        const r = await fn({ empresa_id: empresaId, recebimento_id: recebimento.id });
        const lista = r.data?.data?.items || [];
        if (cancel) return;
        setItems(lista);
        if (lista.length) {
          const sug = httpsCallable(functions, 'sugerirPosicoesPallets');
          const s = await sug({ empresa_id: empresaId, pallet_ids: lista.map((p) => p.id) });
          if (!cancel) setSugestoes(s.data?.data?.sugestoes || []);
        }
      } catch {
        /* sem pallets ou sem vaga livre */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [empresaId, recebimento.id]);

  const gerar = async () => {
    setBusy(true);
    setErr('');
    try {
      const fn = httpsCallable(functions, 'gerarPalletsRecebimento');
      const r = await fn({
        empresa_id: empresaId,
        recebimento_id: recebimento.id,
        numero_pallets: Number(numero) || 1
      });
      const lista = r.data?.data?.items || [];
      setItems(lista);
      if (lista.length) {
        const sug = httpsCallable(functions, 'sugerirPosicoesPallets');
        const s = await sug({ empresa_id: empresaId, pallet_ids: lista.map((p) => p.id) });
        setSugestoes(s.data?.data?.sugestoes || []);
      }
    } catch (e) {
      setErr(e.message || 'Erro ao gerar pallets');
    } finally {
      setBusy(false);
    }
  };

  const confirmar = async (pallet, posicao) => {
    if (!pallet || !posicao) return;
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      const fn = httpsCallable(functions, 'confirmarArmazenagemPallet');
      const r = await fn({
        empresa_id: empresaId,
        pallet_scan: pallet.qr_payload || pallet.codigo,
        posicao_scan: posicao.qr_payload || posicao.endereco || posicao.id
      });
      setMsg(r.data?.data?.message || `Pallet vinculado a ${posicao.endereco}.`);
      setItems((prev) =>
        prev.map((p) => (p.id === pallet.id ? { ...p, status: 'armazenado', endereco: posicao.endereco } : p))
      );
    } catch (e) {
      setErr(e.message || 'Não foi possível vincular a posição.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Warehouse className="h-5 w-5 text-emerald-700" />
            Pallets do recebimento
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex-1 space-y-3 text-sm">
          <p className="text-gray-600">
            {recebimento.produto_nome} · {recebimento.quantidade_recebida} {recebimento.unidade_medida}
          </p>
          <p className="text-xs text-gray-500">
            Cadastre câmaras e posições em Estoque → Armazenamento. Aqui, no recebimento aceito, gere o pallet,
            confirme a posição sugerida e imprima a etiqueta para colar no pallet.
          </p>
          {err && <p className="text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{err}</p>}
          {msg && <p className="text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">{msg}</p>}
          {items.length === 0 ? (
            <div className="space-y-2">
              <label className="block text-sm">
                Quantos pallets foram recebidos?
                <input
                  type="number"
                  min={1}
                  max={80}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                />
              </label>
              <p className="text-xs text-gray-500">
                A quantidade total será dividida igualmente entre os pallets. Cada um recebe uma etiqueta QR.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={gerar}
                className="w-full bg-emerald-600 text-white rounded-lg py-2 font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Gerar pallets e sugerir posições
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((p) => {
                const sug = sugestoes.find((s) => s.pallet?.id === p.id);
                return (
                  <div key={p.id} className="border rounded-lg px-3 py-2">
                    <p className="font-mono font-semibold">{p.codigo}</p>
                    <p className="text-xs text-gray-600">
                      {p.quantidade} {p.unidade} · val. {p.data_validade || '—'} · {p.status}
                    </p>
                    {p.endereco && (
                      <p className="text-xs text-emerald-800 mt-1">Posição confirmada: {p.endereco}</p>
                    )}
                    {sug?.posicao && !p.endereco && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-emerald-800">
                          Sugerido: {sug.posicao.endereco} (câmara {sug.posicao.camara_codigo || '—'})
                        </p>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => confirmar(p, sug.posicao)}
                          className="text-xs font-semibold text-white bg-emerald-700 rounded px-2 py-1 disabled:opacity-50"
                        >
                          Vincular nesta posição
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                className="w-full inline-flex items-center justify-center gap-2 border rounded-lg py-2"
                onClick={() => imprimirEtiquetasQr(items.map(etiquetaPallet), 'Etiquetas de pallet')}
              >
                <Printer className="h-4 w-4" /> Imprimir etiquetas QR
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PalletsRecebimentoModal;
